<template>
  <v-card
    class="need-card"
    :class="{
      'has-response': need.response && profileType === 'Volunteer',
      urgent: need.priority === 'high'
    }"
    elevation="0"
    hover
  >
    <!-- Urgent Priority Banner -->
    <div class="urgent-banner" v-if="need.priority === 'high'">
      <v-icon small class="mr-1">mdi-alert</v-icon>
      <span>URGENT</span>
    </div>

    <!-- Main Card Content -->
    <div class="card-container">
      <!-- Header Section -->
      <div class="card-header">
        <div class="header-main">
          <v-avatar
            :color="need.priority === 'high' ? '#FF5722' : 'primary'"
            size="64"
            class="need-avatar"
            @click="goToDetails"
          >
            <v-icon dark size="28" color="#ffff">mdi-hand-heart</v-icon>
          </v-avatar>

          <div class="header-content" @click="goToDetails">
            <div class="title-section">
              <h3 class="need-title">{{ need.title }}</h3>
              <v-chip
                v-if="need.category"
                small
                outlined
                color="primary"
                class="category-chip"
              >
                {{ need.category }}
              </v-chip>
            </div>

            <div class="meta-section">
              <div class="meta-tags">
                <div class="meta-tag">
                  <v-icon size="16" color="primary">mdi-clock-outline</v-icon>
                  <span>{{ need.duration }}</span>
                </div>
                <div class="meta-tag" v-if="need.location">
                  <v-icon size="16" color="primary">mdi-map-marker</v-icon>
                  <span>{{ need.location }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons in Header -->
        <div class="header-actions">
          <v-menu bottom offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="grey darken-1"
                v-bind="attrs"
                v-on="on"
                class="menu-btn"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="onShare(need)">
                <v-list-item-icon>
                  <v-icon>mdi-share-variant</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Share</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="profileType === 'Agency'" @click="goToDetails">
                <v-list-item-icon>
                  <v-icon>mdi-information-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Details</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <!-- Date Information -->
      <div class="date-section" v-if="need.start_date">
        <div class="date-chips">
          <v-chip
            v-if="
              need.start_date && need.end_date && need.duration !== 'Happens On'
            "
            class="date-chip"
            color="blue"
            outlined
            small
          >
            <v-icon left size="16">mdi-calendar-start</v-icon>
            {{ formatDate(need.start_date) }}
          </v-chip>
          <v-chip
            v-if="need.end_date && need.duration !== 'Happens On'"
            class="date-chip"
            color="green"
            outlined
            small
          >
            <v-icon left size="16">mdi-calendar-end</v-icon>
            {{ formatDate(need.end_date) }}
          </v-chip>
          <v-chip
            v-if="need.start_date && need.duration === 'Happens On'"
            class="date-chip"
            color="primary"
            outlined
            small
          >
            <v-icon left size="16">mdi-calendar</v-icon>
            {{ formatDate(need.start_date) }}
          </v-chip>
        </div>
      </div>
      <v-btn
        v-if="profileType === 'Volunteer'"
        :color="need.response ? 'success' : 'primary'"
        elevation="2"
        rounded
        class="respond-btn"
        @click.stop="goToDetails"
      >
        <v-icon left size="18">
          {{ need.response ? "mdi-check-circle" : "mdi-hand-heart" }}
        </v-icon>
        {{ need.response ? "View Response" : "Respond" }}
      </v-btn>

      <!-- Description Section -->
      <div class="description-section">
        <div
          class="description-text"
          :class="{ collapsed: text_collapsed }"
          v-linkified
        >
          {{ text_collapsed ? collapsed_text : need.description }}
        </div>
        <v-btn
          v-if="text_collapsed && collapsed_text.length > 0"
          text
          small
          color="primary"
          class="read-more-btn"
          @click.stop="text_collapsed = false"
        >
          Read More
          <v-icon right size="16">mdi-chevron-down</v-icon>
        </v-btn>
      </div>

      <!-- Response Status -->
      <div
        class="status-section"
        v-if="need.response && profileType === 'Volunteer'"
      >
        <div class="status-card">
          <div
            class="status-indicator"
            :class="need.response.status.toLowerCase()"
          >
            <v-icon :color="getStatusColor(need.response.status)" size="20">
              {{ getStatusIcon(need.response.status) }}
            </v-icon>
          </div>
          <div class="status-content">
            <div class="status-text">
              <span class="status-label">Your response:</span>
              <span
                class="status-value"
                :style="{ color: getStatusColor(need.response.status) }"
              >
                {{ need.response.status }}
              </span>
            </div>
            <div class="status-date" v-if="need.response.date">
              {{ formatShortDate(need.response.date) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Shifts Section -->
      <div class="shifts-section" v-if="need.shifts">
        <div class="section-divider"></div>

        <div class="shifts-header">
          <div class="shifts-title">
            <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
            <h4>Available Shifts</h4>
          </div>
          <div class="shifts-summary">
            <v-chip
              small
              color="primary"
              outlined
              @click="goToDetails"
              class="shifts-count-chip"
            >
              {{ need.shifts.length }} shift{{
                need.shifts.length !== 1 ? "s" : ""
              }}
            </v-chip>
            <v-btn
              v-if="
                need.responses &&
                  need.responses.length &&
                  profileType === 'Agency'
              "
              text
              small
              color="primary"
              class="responses-btn"
              @click.stop="goToResponse"
            >
              {{ need.responses.length }} response{{
                need.responses.length !== 1 ? "s" : ""
              }}
              <v-badge
                v-if="getPendingCount > 0"
                color="orange"
                :content="getPendingCount"
                inline
                class="ml-1"
              ></v-badge>
              <v-icon right size="16">mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- No Shifts Alert -->
        <v-alert
          v-if="need.shifts.length === 0"
          dense
          color="warning"
          outlined
          class="no-shifts-alert"
        >
          <div class="alert-content">
            <v-icon color="warning" class="mr-2">mdi-alert-circle</v-icon>
            <span
              >No active shifts available. This opportunity is not visible to
              volunteers.</span
            >
          </div>
        </v-alert>

        <!-- Shifts Display -->
        <div class="shifts-container" v-else>
          <!-- Display Mode Toggle -->
          <div class="display-toggle">
            <v-btn-toggle
              v-model="shiftDisplayMode"
              mandatory
              dense
              rounded
              color="primary"
              class="toggle-buttons"
            >
              <v-btn value="list" small>
                <v-icon left size="16">mdi-format-list-bulleted</v-icon>
                List
              </v-btn>
              <v-btn value="calendar" small>
                <v-icon left size="16">mdi-calendar-month</v-icon>
                Calendar
              </v-btn>
            </v-btn-toggle>
          </div>

          <!-- List View -->
          <div v-if="shiftDisplayMode === 'list'" class="shifts-list">
            <div
              v-for="(shift, index) in limitedShifts"
              :key="shift._id"
              class="shift-card"
              :class="{
                highlighted: isShiftHighlighted(shift),
                full: isShiftFull(shift)
              }"
              @click="goToDetails"
            >
              <div class="shift-header">
                <div class="shift-info">
                  <h5 class="shift-title">
                    {{ shift.title || `Shift ${index + 1}` }}
                    <v-chip
                      v-if="shift.skill_level"
                      x-small
                      outlined
                      color="secondary"
                      class="ml-2"
                    >
                      {{ shift.skill_level }}
                    </v-chip>
                  </h5>
                  <div class="shift-datetime">
                    <v-icon size="16" color="grey darken-1" class="mr-1"
                      >mdi-clock</v-icon
                    >
                    <span class="datetime-text">
                      <template v-if="need.duration === 'Happens On'">
                        {{ formatDateTime(shift.start_date) }}
                      </template>
                      <template v-else>
                        {{ formatDateTime(shift.start_date) }}
                        {{
                          shift.hours_description
                            ? `(${shift.hours_description})`
                            : `(${formatTime(shift.start_date)} - ${formatTime(
                                new Date(
                                  new Date(shift.start_date).getTime() +
                                    shift.hours * 60 * 60 * 1000
                                )
                              )})`
                        }}
                      </template>
                    </span>
                  </div>
                </div>

                <div class="shift-capacity">
                  <div class="capacity-indicator">
                    <v-progress-circular
                      :value="getShiftFillPercentage(shift)"
                      :color="getShiftAvailabilityColor(shift)"
                      size="48"
                      width="4"
                      rotate="270"
                    >
                      <span class="capacity-number">
                        {{ shift.capacity - getAcceptedResponsesCount(shift) }}
                      </span>
                    </v-progress-circular>
                  </div>
                  <div class="capacity-text">
                    <div class="capacity-available">
                      of {{ shift.capacity }} available
                    </div>
                    <div
                      class="capacity-status"
                      :style="{ color: getShiftAvailabilityColor(shift) }"
                    >
                      {{ getShiftAvailabilityText(shift) }}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <!-- Calendar View -->
          <div v-if="shiftDisplayMode === 'calendar'" class="shifts-calendar">
            <v-calendar
              ref="calendar"
              type="month"
              :events="shiftEvents"
              :event-color="getEventColor"
              :event-height="40"
              @click:event="showShiftDetails"
              class="modern-calendar"
            >
              <template v-slot:event="{ event }">
                <div class="calendar-event">
                  <div class="event-title">{{ event.name }}</div>
                  <div class="event-slots">
                    {{ event.slots }} slot{{ event.slots !== 1 ? "s" : "" }}
                  </div>
                </div>
              </template>
            </v-calendar>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialogs -->
    <v-dialog v-model="shiftDialog" max-width="600" persistent>
      <v-card v-if="selectedShift" class="shift-dialog">
        <v-card-title class="dialog-header">
          <h3>{{ selectedShift.title || "Shift Details" }}</h3>
          <v-spacer></v-spacer>
          <v-btn icon @click="shiftDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <div class="dialog-info-grid">
            <div class="info-item">
              <v-icon left color="primary">mdi-clock</v-icon>
              <div>
                <div class="info-label">Date & Time</div>
                <div class="info-value">
                  {{ formatDateTime(selectedShift.start_date) }}
                </div>
              </div>
            </div>

            <div class="info-item">
              <v-icon left color="primary">mdi-account-group</v-icon>
              <div>
                <div class="info-label">Availability</div>
                <div class="info-value">
                  {{
                    selectedShift.capacity -
                      getAcceptedResponsesCount(selectedShift)
                  }}
                  of {{ selectedShift.capacity }} slots available
                </div>
              </div>
            </div>

            <div class="info-item" v-if="selectedShift.description">
              <v-icon left color="primary">mdi-information</v-icon>
              <div>
                <div class="info-label">Description</div>
                <div class="info-value">{{ selectedShift.description }}</div>
              </div>
            </div>

            <div class="info-item" v-if="selectedShift.requirements">
              <v-icon left color="primary">mdi-clipboard-list</v-icon>
              <div>
                <div class="info-label">Requirements</div>
                <div class="info-value">{{ selectedShift.requirements }}</div>
              </div>
            </div>

            <div class="info-item" v-if="selectedShift.location">
              <v-icon left color="primary">mdi-map-marker</v-icon>
              <div>
                <div class="info-label">Location</div>
                <div class="info-value">{{ selectedShift.location }}</div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="shiftDialog = false">Close</v-btn>
          <v-btn color="primary" elevation="2" @click="goToDetails">
            View Full Details
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" top timeout="4000">
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Other Dialogs -->
    <agency-edit-need
      :item="selected"
      :dialog="editDialog"
      :onCloseDialog="onCloseEditDialog"
      v-if="selected && editDialog"
    />

    <confirm-delete-need-dialog
      :deleteDialog="deleteDialog"
      :need="selected"
      :onClose="onCloseConfirmDelete"
      :onDeleted="onDeletedNeed"
      v-if="selected && deleteDialog"
    />

    <need-share-dialog-vue
      :dialog="shareDialog"
      :onClose="onCloseShareDialog"
      :eventUrlData="this.eventUrlData"
    />
  </v-card>
</template>

<script>
import AgencyEditNeed from "./create/AgencyEditNeed.vue";
import ConfirmDeleteNeedDialog from "@/components/common/ConfirmDeleteNeedDialog.vue";
import { mapState } from "vuex";
import NeedShareDialogVue from "./NeedShareDialog.vue";

export default {
  components: {
    AgencyEditNeed,
    ConfirmDeleteNeedDialog,
    NeedShareDialogVue
  },
  props: {
    need: Object,
    hideButtons: {
      type: Boolean,
      default: false
    },
    onClicked: Function,
    editable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      snackbar: false,
      snackbarColor: "primary",
      snackMessage: null,
      text_collapsed: true,
      collapsed_text: "",
      editDialog: false,
      deleteDialog: false,
      selected: null,
      shiftDialog: false,
      selectedShift: null,
      expandedShift: null,
      shareDialog: false,
      eventUrlData: {},
      shiftDisplayMode: "list"
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile",
      profileType: "type"
    }),
    getPendingCount() {
      if (!this.need.responses) return 0;
      return this.need.responses.filter(r => r.status == "Pending").length;
    },
    shiftEvents() {
      if (!this.need.shifts) return [];
      return this.need.shifts.map(shift => ({
        id: shift._id,
        name: shift.title || "Shift",
        start: new Date(shift.start_date),
        end: new Date(shift.end_date),
        slots: shift.capacity - this.getAcceptedResponsesCount(shift),
        timed: true,
        shift: shift
      }));
    },
      limitedShifts() {
    // Only show the first 10 shifts
    return this.need.shifts ? this.need.shifts.slice(0, 10) : [];
  }
  },
  methods: {
    getAcceptedResponsesCount(shift) {
      if (!this.need.responses) return 0;
      return this.need.responses.filter(
        r => r.shift_id === shift._id && r.status !== "Rejected"
      ).length;
    },
    getShiftFillPercentage(shift) {
      const filled = this.getAcceptedResponsesCount(shift);
      return (filled / shift.capacity) * 100;
    },
    getShiftAvailabilityColor(shift) {
      const available = shift.capacity - this.getAcceptedResponsesCount(shift);
      if (available === 0) return "error";
      if (available <= 2) return "warning";
      return "success";
    },
    getShiftAvailabilityText(shift) {
      const available = shift.capacity - this.getAcceptedResponsesCount(shift);
      if (available === 0) return "Fully booked";
      if (available <= 2) return "Almost full";
      return "Available";
    },
    isShiftHighlighted(shift) {
      const now = new Date();
      const shiftStart = new Date(shift.start_date);
      const hoursUntilStart = (shiftStart - now) / (1000 * 60 * 60);
      return hoursUntilStart < 24;
    },
    isShiftFull(shift) {
      return this.getAcceptedResponsesCount(shift) >= shift.capacity;
    },
    getEventColor(event) {
      if (event.slots === 0) return "red";
      if (event.slots <= 2) return "orange";
      return "primary";
    },
    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case "pending":
          return "#FF9800";
        case "accepted":
          return "#4CAF50";
        case "rejected":
          return "#F44336";
        default:
          return "#9E9E9E";
      }
    },
    getStatusIcon(status) {
      switch (status.toLowerCase()) {
        case "pending":
          return "mdi-clock";
        case "accepted":
          return "mdi-check-circle";
        case "rejected":
          return "mdi-close-circle";
        default:
          return "mdi-information";
      }
    },
    toggleShiftExpand(shift) {
      this.expandedShift = this.expandedShift === shift._id ? null : shift._id;
    },
    showShiftDetails({ event }) {
      this.selectedShift = event.shift;
      this.shiftDialog = true;
    },
    onCloseShareDialog() {
      this.shareDialog = false;
    },
    goToResponse() {
      this.$router.push({
        name: "agency-responses-feeds",
        query: { _id: this.need._id }
      });
    },
    goToDetails() {
      if (!this.onClicked) {
        this.$router.push({
          name: "agency-need-details",
          query: {
            _id: this.need._id
          }
        });
      } else {
        this.onClicked(this.need._id);
      }
    },
    async onShare(need) {
      this.shareDialog = true;
      this.eventUrlData = need;
    },
    getCollapsedText() {
      if (!this.need.description) {
        this.text_collapsed = false;
        return;
      }
      const full_text = this.need.description.trim();
      const textArray = full_text.split(/\r\n|\r|\n/);

      if (textArray.length > 3) {
        this.collapsed_text = textArray.slice(0, 3).join("\n");
        this.text_collapsed = true;
        return;
      }

      if (full_text.length > 200) {
        this.collapsed_text = full_text.substring(0, 200) + "...";
        this.text_collapsed = true;
        return;
      }

      this.collapsed_text = full_text;
      this.text_collapsed = false;
    },
    onEdit(need) {
      this.selected = need;
      this.editDialog = true;
    },
    onCloseEditDialog() {
      this.editDialog = false;
    },
    onDelete(need) {
      this.selected = need;
      this.deleteDialog = true;
    },
    onCloseConfirmDelete() {
      this.deleteDialog = false;
      this.deleteShiftDialog = false;
    },
    onDeletedNeed() {
      this.deleteDialog = false;
    },
    formatDate(date) {
      if (!date) return "";
      // Parse the ISO date string (e.g., "2025-05-31T00:00:00.000+00:00")
      const d = new Date(date);
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const year = d.getFullYear();
      return `${month}/${day}/${year}`;
    },
    formatShortDate(date) {
      if (!date) return "";
      const tz = localStorage.getItem("user-timezone") || "UTC";
      const options = {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: tz
      };
      return new Date(date).toLocaleDateString(undefined, options);
    },
    formatDateTime(date) {
      if (!date) return "";
      const d = typeof date === "string" ? new Date(date) : date;
      const localDate = this.convertTZ(d, this.timezone);
      return localDate.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    },
    formatTime(date) {
      if (!date) return "";
      const tz = localStorage.getItem("user-timezone") || "UTC";
      const options = {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: tz
      };
      return new Date(date).toLocaleTimeString(undefined, options);
    },
    convertTZ(date, tzString) {
      if (!date) return "";
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          {
            timeZone: tzString ? tzString : "America/New_York"
          }
        )
      );
    }
  },
  mounted() {
    this.getCollapsedText();
  }
};
</script>

<style scoped lang="scss">
// .need-card {
//   border-radius: 20px;
//   transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
//   margin-bottom: 20px;
//   overflow: hidden;
//   position: relative;
//   border: 1px solid rgba(0, 0, 0, 0.06);
//   background: #fff;
//   background-size: cover;
//   box-shadow: 0 4px 12px rgb(255, 255, 255);
//   opacity: 0.4;

//     &::before {
//     content: "";
//     position: absolute;
//     top: 0; left: 0; right: 0; bottom: 0;
//     background: url('~@/assets/need-card-bg.jpg') no-repeat center center;
//     // background-size: cover;
//     opacity: 0.15; // Adjust for desired dullness
//     z-index: 0;    // Behind content
//   }

//   &:hover {
//     transform: translateY(-8px);
//     box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08) !important;
//     border-color: rgba(var(--v-primary-base), 0.2);
//   }

//   &.has-response {
//     border-left: 4px solid var(--v-primary-base);
//     background: linear-gradient(
//       135deg,
//       rgba(33, 150, 243, 0.02) 0%,
//       rgba(33, 150, 243, 0.06) 100%
//     );
//   }

//   &.urgent {
//     border-left: 4px solid #ff5722;
//     background: linear-gradient(
//       135deg,
//       rgba(255, 87, 34, 0.02) 0%,
//       rgba(255, 87, 34, 0.06) 100%
//     );
//   }
// }

.need-card {
  position: relative;
  border-radius: 20px;
  margin-bottom: 20px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);

  // Content stays clear
  z-index: 1;
  background-color: #fff;

  // Overlay background image using ::before
  // &::before {
  //   content: "";
  //   position: absolute;
  //   top: 220px; left: 0; right: 0; bottom: 20px;
  //   background: url('~@/assets/need-card-bg.jpg') no-repeat center center;
  //   // background-size: cover;
  //   opacity: 0.1; // Adjust for desired dullness
  //   z-index: 0;    // Behind content
  // }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08) !important;
    border-color: rgba(var(--v-primary-base), 0.2);
  }

  &.has-response {
    border-left: 4px solid var(--v-primary-base);
    background: linear-gradient(
      135deg,
      rgba(33, 150, 243, 0.02) 0%,
      rgba(33, 150, 243, 0.06) 100%
    );
  }

  &.urgent {
    border-left: 4px solid #ff5722;
    background: linear-gradient(
      135deg,
      rgba(255, 87, 34, 0.02) 0%,
      rgba(255, 87, 34, 0.06) 100%
    );
  }
}


.urgent-banner {
  position: absolute;
  top: 0;
  right: 0;
  background: linear-gradient(135deg, #ff5722, #e64a19);
  color: white;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom-left-radius: 12px;
  box-shadow: 0 4px 8px rgba(255, 87, 34, 0.3);
  z-index: 2;
  display: flex;
  align-items: center;
}

.card-container {
  padding: 24px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;

  .header-main {
    display: flex;
    align-items: flex-start;
    flex: 1;
    min-width: 0;

    .need-avatar {
      margin-right: 16px;
      flex-shrink: 0;
      background: linear-gradient(135deg, #084468, #0a5b8a);
      box-shadow: 0 6px 20px rgba(var(--v-primary-base), 0.3);
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 8px 25px rgba(var(--v-primary-base), 0.4);
      }
    }

    .header-content {
      flex: 1;
      min-width: 0;
      cursor: pointer;

      .title-section {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
        margin-bottom: 12px;

        .need-title {
          font-size: 1.4rem;
          font-weight: 700;
          color: #095079;
          margin: 0;
          line-height: 1.3;
          word-break: break-word;
        }

        .category-chip {
          font-weight: 600;
          font-size: 0.75rem;
          letter-spacing: 0.5px;
        }

        .meta-section {
          display: flex;
          flex-direction: column;
          gap: 8px;

          .meta-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;

            .meta-tag {
              display: flex;
              align-items: center;
              font-size: 0.875rem;
              color: #616161;

              span {
                margin-left: 4px;
              }
            }
          }
        }
      }
    }
  }

  .need-title:hover {
    color: #269ebc;
  }
}
.header-actions {
  display: flex;
  align-items: center;
  margin-left: 16px;

  .respond-btn {
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0.5px;
    padding: 8px 16px;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .menu-btn {
    margin-left: 8px;
  }
}

.date-section {
  margin-bottom: 16px;

  .date-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .date-chip {
      font-weight: 500;
      letter-spacing: 0.5px;
    }
  }
}

.description-section {
  margin-bottom: 20px;
  position: relative;

  .description-text {
    white-space: pre-line;
    color: #424242;
    line-height: 1.6;
    font-size: 0.9375rem;

    &.collapsed {
      max-height: 120px;
      overflow: hidden;
      mask-image: linear-gradient(to bottom, black 50%, transparent 100%);
    }
  }

  .read-more-btn {
    margin-top: 8px;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
}

.status-section {
  margin-bottom: 20px;

  .status-card {
    display: flex;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 12px;
    padding: 12px 16px;
    border-left: 4px solid;

    .status-indicator {
      margin-right: 12px;
      display: flex;
      align-items: center;
      justify-content: center;

      &.pending {
        color: #ff9800;
      }
      &.accepted {
        color: #4caf50;
      }
      &.rejected {
        color: #f44336;
      }
    }

    .status-content {
      flex: 1;

      .status-text {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 4px;

        .status-label {
          font-size: 0.875rem;
          color: #616161;
        }

        .status-value {
          font-weight: 600;
          text-transform: capitalize;
          font-size: 0.9375rem;
        }
      }

      .status-date {
        font-size: 0.75rem;
        color: #9e9e9e;
      }
    }
  }
}

.shifts-section {
  .section-divider {
    height: 1px;
    background-color: rgba(0, 0, 0, 0.08);
    margin: 16px 0;
  }

  .shifts-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .shifts-title {
      display: flex;
      align-items: center;
      color: #1a1a1a;

      h4 {
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0;
      }
    }

    .shifts-summary {
      display: flex;
      align-items: center;
      gap: 12px;

      .shifts-count-chip {
        font-weight: 600;
        cursor: pointer;
      }

      .responses-btn {
        font-weight: 600;
        letter-spacing: 0.5px;
      }
    }
  }

  .no-shifts-alert {
    border-radius: 12px;
    margin-bottom: 16px;

    .alert-content {
      display: flex;
      align-items: center;
    }
  }

  .shifts-container {
    .display-toggle {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 12px;

      .toggle-buttons {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
    }

    .shifts-list {
      display: flex;
      flex-direction: column;
      gap: 12px;

      .shift-card {
        background-color: white;
        border-radius: 12px;
        padding: 16px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        transition: all 0.3s ease;
        border: 1px solid rgba(0, 0, 0, 0.05);

        cursor: pointer;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
        }

        &.highlighted {
          border-left: 4px solid #ff9800;
          background-color: rgba(255, 152, 0, 0.03);
        }

        &.full {
          opacity: 0.7;
        }

        .shift-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;

          .shift-info {
            flex: 1;
            min-width: 0;

            .shift-title {
              font-size: 1rem;
              font-weight: 600;
              margin: 0 0 8px 0;
              display: flex;
              align-items: center;
            }

            .shift-datetime {
              display: flex;
              align-items: center;
              font-size: 0.875rem;
              color: #616161;
            }
          }

          .shift-capacity {
            display: flex;
            align-items: center;
            margin-left: 16px;

            .capacity-indicator {
              position: relative;
              margin-right: 12px;

              .capacity-number {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                font-weight: 600;
                font-size: 0.875rem;
                color: #1a1a1a;
              }
            }

            .capacity-text {
              display: flex;
              flex-direction: column;

              .capacity-available {
                font-size: 0.75rem;
                color: #9e9e9e;
              }

              .capacity-status {
                font-size: 0.75rem;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
            }
          }
        }

        .shift-details {
          padding: 12px 0;
          border-top: 1px dashed rgba(0, 0, 0, 0.1);
          margin-top: 12px;

          .detail-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 8px;
            font-size: 0.875rem;
            color: #424242;
            line-height: 1.5;
          }

          .detail-actions {
            display: flex;
            justify-content: flex-end;
            margin-top: 12px;
          }
        }

        .shift-toggle {
          display: flex;
          justify-content: center;

          .toggle-btn {
            font-weight: 600;
          }
        }
      }
    }

    .shifts-calendar {
      margin-top: 16px;

      .modern-calendar {
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

        .calendar-event {
          padding: 4px 8px;
          font-size: 0.75rem;
          line-height: 1.3;

          .event-title {
            font-weight: 600;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .event-slots {
            font-size: 0.6875rem;
            opacity: 0.8;
          }
        }
      }
    }
  }
}

.shift-dialog {
  border-radius: 16px !important;
  overflow: hidden;

  .dialog-header {
    padding: 24px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);

    h3 {
      font-weight: 600;
      margin: 0;
    }
  }

  .dialog-content {
    padding: 24px;

    .dialog-info-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 16px;

      .info-item {
        display: flex;
        align-items: flex-start;

        .info-label {
          font-size: 0.875rem;
          color: #616161;
          margin-bottom: 4px;
        }

        .info-value {
          font-size: 0.9375rem;
          color: #1a1a1a;
          line-height: 1.5;
        }
      }
    }
  }

  .dialog-actions {
    padding: 16px 24px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
}

@media (max-width: 600px) {
  .card-header {
    flex-direction: column;

    .header-actions {
      margin-left: 0;
      margin-top: 16px;
      width: 100%;
      justify-content: space-between;
    }
  }

  .shifts-header {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 12px;

    .shifts-summary {
      width: 100%;
      justify-content: space-between;
    }
  }

  .shift-header {
    flex-direction: column;
    gap: 16px;

    .shift-capacity {
      margin-left: 0 !important;
      justify-content: flex-start;
      width: 100%;
    }
  }
}
</style>
